
import Vue from "vue";
import MainBanner from "@/components/MainBanner.vue";
import SideBar from "../components/SideBar.vue";
import GamesGrid from "@/components/GamesGrid.vue";
import BrandSelector from "@/components/BrandSelector.vue";
import GameTypeFilter from "@/components/GameTypeFilter.vue";
import { getCurrentBrand } from "@/brandService";
import { getBrandConfig, loadBrandConfigs, isBrandConfigLoaded } from "@/services/brandConfigService";
import { Category } from "@/config/types";
import { getLauncherUrl } from "@/services/gameLauncher";
import { getSubFilters, SubFilterOption, SUB_FILTER_CONFIG } from "@/config/subFilters";
import { fetchGameFile } from "@/services/gameDataService";

export default Vue.extend({
  name: "MyHome",

  components: {
    MainBanner,
    SideBar,
    GamesGrid,
    BrandSelector,
    GameTypeFilter,
  },
  data() {
    return {
      selectedCategory: null as string | null,
      previousCategory: null as string | null,
      searchQuery: "" as string,
      selectedGameData: null as any,
      mobileMenuOpen: false as boolean,
      mobileSearchOpen: false as boolean,
      toast: "" as string,
      toastVisible: false as boolean,
      activeBrandFilter: null as string | null,
      activeTypeFilter: null as string | null,
      activeIntegration: null as string | null,
      launchingGameName: null as string | null,
      gameDataCache: {} as Record<string, any>,
    };
  },
  computed: {
    currentBrandId(): string {
      try {
        return getCurrentBrand();
      } catch {
        return "";
      }
    },
    hasBrands(): boolean {
      const brandsConfig = getBrandConfig(this.currentBrandId);
      if(brandsConfig.categories.length == 1) return false;
      return brandsConfig.categories.some(
        (c: Category) => !!c.brandIcon
      );
    },
    sidebarSubFilters(): SubFilterOption[] {
      return getSubFilters(this.currentBrandId, this.selectedCategory)
        .filter(f => f.field === 'brand');
    },
    chipSubFilters(): SubFilterOption[] {
      // When the category has brand IP sub-filters, use those as chips
      if (this.sidebarSubFilters.length > 0) {
        return this.sidebarSubFilters.filter((sf: SubFilterOption) => !sf.comingSoon);
      }

      // Otherwise compute gameType chips from cached game data
      const config = getBrandConfig(this.currentBrandId);
      const categories = this.selectedCategory
        ? config.categories.filter((c: Category) => c.name === this.selectedCategory)
        : config.categories;

      let allGames: any[] = [];
      for (const cat of categories) {
        const raw = this.gameDataCache[cat.gamesPath];
        if (!raw) continue;
        let catGames: any[];
        if (Array.isArray(raw)) {
          catGames = raw;
        } else {
          catGames = cat.ipKeys
            ? cat.ipKeys.flatMap((key: string) => (raw[key] || []) as any[])
            : (Object.values(raw) as any[][]).flat();
        }
        allGames.push(...catGames.filter(
          (g: any) => !g.accessOnly || g.accessOnly.includes(this.currentBrandId)
        ));
      }

      const filtered = this.activeBrandFilter
        ? allGames.filter((g: any) => g.brand === this.activeBrandFilter)
        : allGames;

      if (filtered.length < 5) return [];

      const types = [...new Set(filtered.map((g: any) => g.gameType).filter(Boolean))] as string[];
      if (types.length <= 1) return [];

      return types.map(t => ({ label: t, field: 'gameType' as const, value: t }));
    },
    activeChipFilter(): string | null {
      if (this.sidebarSubFilters.length > 0) {
        return this.activeBrandFilter ?? '__all__';
      }
      return this.activeTypeFilter;
    },
    subFilterableCategories(): string[] {
      return Object.keys(SUB_FILTER_CONFIG)
        .filter((k: string) => k.startsWith(this.currentBrandId + '::'))
        .map((k: string) => k.split('::')[1]);
    },
  },
  watch: {
    currentBrandId: {
      immediate: true,
      handler(brandId: string) {
        if (brandId) this.loadGameDataCache();
      },
    },
  },
  methods: {
    async loadGameDataCache() {
      const config = getBrandConfig(this.currentBrandId);
      await Promise.all(config.categories.map(async (cat: Category) => {
        try {
          const data = await fetchGameFile(cat.gamesPath);
          this.$set(this.gameDataCache, cat.gamesPath, data);
        } catch { /* skip */ }
      }));
    },
    scrollToTop() {
      const el = this.$refs.mainContent as HTMLElement;
      if (el) el.scrollTop = 0;
      window.scrollTo(0, 0);
    },
    handleCategorySelection(categoryName: string | null) {
      this.selectedCategory = categoryName;
      this.activeBrandFilter = null;
      this.activeTypeFilter = null;
      this.activeIntegration = null;
      this.closeMobileSearch();
      this.closeMobileSidebar();
      this.$nextTick(() => {
        this.scrollToTop();
        setTimeout(() => this.scrollToTop(), 50);
      });
    },
    handleGameSelection(categoryName: string | null) {
      this.selectedCategory = categoryName;
      this.selectedGameData = null;
      this.activeBrandFilter = null;
      this.activeTypeFilter = null;
      this.activeIntegration = null;
      this.closeMobileSearch();
      document.body.classList.remove('game-selected');

      // Only close the mobile sidebar if the category has no sub-filters (accordion behavior)
      const hasSubFilters = categoryName ? this.subFilterableCategories.includes(categoryName) : false;
      if (!hasSubFilters) {
        this.closeMobileSidebar();
      }

      this.$nextTick(() => {
        this.scrollToTop();
        setTimeout(() => this.scrollToTop(), 50);
      });
    },
    handleResetSelection() {
      this.selectedCategory = null;
      this.selectedGameData = null;
      this.activeBrandFilter = null;
      this.activeTypeFilter = null;
      this.activeIntegration = null;
      document.body.classList.remove('game-selected');
    },
    onBrandFilterChanged(value: string | null) {
      this.activeBrandFilter = value;
      this.activeTypeFilter = null;
    },
    onIntegrationFilterChanged(value: string | null) {
      this.activeIntegration = value;
      this.selectedCategory = null;
      this.selectedGameData = null;
      this.activeBrandFilter = null;
      this.activeTypeFilter = null;
      document.body.classList.remove('game-selected');
    },
    onChipFilterChanged(value: string | null) {
      if (this.sidebarSubFilters.length > 0) {
        this.activeBrandFilter = (value === '__all__' || value === null) ? null : value;
        this.activeTypeFilter = null;
      } else {
        this.activeTypeFilter = value;
      }
    },
    handleGoBack() {
      this.selectedGameData = null;
      this.selectedCategory = this.previousCategory;
      document.body.classList.remove('game-selected');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    closeMobileSidebar() {
      this.mobileMenuOpen = false;

      // Re-enable body scroll
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.height = "";
    },
    openMobileSearch() {
      this.mobileSearchOpen = true;
      this.$nextTick(() => {
        (this.$refs.mobileSearchInput as HTMLInputElement)?.focus();
      });
    },
    closeMobileSearch() {
      this.mobileSearchOpen = false;
      this.searchQuery = '';
    },
    handleGameClick(game: any) {
      console.log(this.currentBrandId)
      if (this.currentBrandId === 'jogoglobal' || this.currentBrandId === 'jogoglobal2'

      || this.currentBrandId.includes("demo")
      ) {
        this.launchGameDirectly(game);
        return;
      }
      this.previousCategory = this.selectedCategory;
      this.selectedGameData = game;
      document.body.classList.add('game-selected');

      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        if (window.scrollY > 0) {
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
      }, 100);
    },
    async launchGameDirectly(game: any) {
      if (this.launchingGameName) return;
      const files = (game.gameFiles || []) as any[];
      const active = files.find((f: any) =>
        (f.actionType === 'api-launch' && (f.gameId || f.url)) ||
        (f.actionType === 'button' && !!f.url && f.action !== 'Download')
      );
      if (!active) {
        this.showToast('Game unavailable');
        return;
      }
      if (active.actionType === 'button' && active.url) {
        window.open(active.url, '_blank');
      } else if (active.actionType === 'api-launch' && active.gameId) {
        this.launchingGameName = game.name;
        try {
          const url = await getLauncherUrl(active.gameId, active.launchProvider, active.url);
          window.open(url, '_blank');
        } catch (e: any) {
          this.showToast(e?.message || 'Failed to launch game');
        } finally {
          this.launchingGameName = null;
        }
      }
    },
    showToast(msg: string) {
      this.toast = msg;
      this.toastVisible = true;
      setTimeout(() => { this.toastVisible = false; }, 3000);
    },
  },
  async created() {
    if (!this.currentBrandId) {
      this.$router.replace("/login");
      return;
    }
    if (!isBrandConfigLoaded(this.currentBrandId)) {
      await loadBrandConfigs();
    }
  },
  mounted() {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.height = "";
    document.body.style.width = "";
    document.body.classList.remove('game-selected');

    this.$root.$on("mobile-menu-toggle", (isOpen: boolean) => {
      this.mobileMenuOpen = isOpen;

      if (isOpen) {
        // Disable body scroll when sidebar opens
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.height = "100%";
        document.body.style.width = "100%";
      } else {
        // Re-enable body scroll when sidebar closes
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.height = "";
        document.body.style.width = "";
      }
    });

  },
  beforeDestroy() {
    this.$root.$off("mobile-menu-toggle");
  },
});
